/* eslint-disable react/no-string-refs */
import React from 'react';
import { PropTypes as PT } from 'prop-types';
import ReactInfinite from 'react-infinite';
import { FormattedMessage } from 'react-intl';
import _findKey from 'lodash/findKey';
import _get from 'lodash/get';
import Scroll from 'react-scroll';
import SVG from 'react-inlinesvg';
import roomSystemIcon from '@lifesize/ux-assets/vectors/svgIcons/room-system.svg';
import ContactListItem from 'components/Contacts/ContactListItem';
import Spinner from 'components/MainSpinner/MainSpinner';
import ContactCard from 'components/Contacts/ContactCard';
import { routes } from 'constants/Application';
import { REACT_INFINITE_LOAD_BEGIN_OFFSET } from 'constants/Contact';
import { participantFilterType } from 'constants/ParticipantList';
import RemoteModeratorPanel from 'containers/Contacts/RemoteModeratorContainer';
import { selectContact } from 'utils/contact-utils';
import classes from 'components/Contacts/Contacts.module.scss';

const CONTACT_HEIGHT = 51;

class RoomSystems extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      height: null,
      participantFilter: participantFilterType.none
    };

    this.selectedUuid = _get(this.props, 'match.params.selectedUuid');
  }

  componentDidMount() {
    this.handleResize = this.handleResize.bind(this);
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    // if you are just navigating within roomSystems (selecting different roomSystems) skip loading
    if (this.props.previousPath && routes.roomSystems !== this.props.previousPath.substr(0, routes.roomSystems.length)) {
      this.props.endRouteLoading();
      this.props.loadInitialRooms();
    }
    const selectedContact = this.props.getSelectedContact(this.selectedUuid);
    if (selectedContact) {
      this.scrollToContact(this.selectedUuid);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleSelectContact = (contact) => {
    selectContact(contact, this.props.history.push);
  };

  renderRightPanel = () => {
    const selectedContact = this.props.getSelectedContact(this.selectedUuid);
    const userCanRemoteModerate = this.props.userCanRemoteModerate(this.selectedUuid);
    const remoteModerationIsActiveForThisMeeting = this.props.remoteModerationIsActiveForThisMeeting(this.selectedUuid);

    if (remoteModerationIsActiveForThisMeeting) {
      return (
        <RemoteModeratorPanel
          contact={selectedContact}
          handleRemoteModerate={this.props.handleRemoteModerate}
          participantFilter={this.state.participantFilter}
          selectParticipantFilter={this.selectParticipantFilter}
        />
      );
    }
    if (!!selectedContact) {
      return (
        <div className={classes.rightContainer}>
          <ContactCard
            contact={selectedContact}
            currentUserId={this.props.currentUserId}
            handleAudioCall={this.props.handleAudioCall}
            handleRemoteModerate={this.props.handleRemoteModerate}
            handleSchedule={this.props.handleSchedule}
            handleVideoCall={this.props.handleVideoCall}
            remoteModerationIsActive={this.props.remoteModerationIsActive}
            remoteModerationIsActiveForThisMeeting={remoteModerationIsActiveForThisMeeting}
            unselectContact={() => this.props.history.push(routes.roomSystems)}
            userCanRemoteModerate={userCanRemoteModerate}
          />
        </div>
      );
    }
    return null;
  }

  scrollToContact(selectedId) {
    setTimeout(() => {
      const index = parseInt(_findKey(this.props.contacts, o => o.id === selectedId), 10);
      const scroller = Scroll.animateScroll;

      scroller.scrollTo(index * CONTACT_HEIGHT, {
        duration: 0,
        smooth: false,
        containerId: 'contactsListContainer',
        isDynamic: true,
        ignoreCancelEvents: true
      });
    }, 0);
  }

  selectParticipantFilter = (participantFilter) => {
    this.setState({
      participantFilter
    });
  }

  handleResize() {
    // eslint-disable-next-line react/no-string-refs
    this.setState({ height: this.refs.container.clientHeight });
  }


  render() {
    const contactsCount = this.props.contacts.length;
    const selectedContact = this.props.getSelectedContact(this.selectedUuid);

    return (
      <div className={classes.contactsContainer}>
        <div className={classes.leftContainer}>
          <div className={classes.contactsHeader}>
            <header>
              <div className={classes.headerIconContainer}>
                <SVG src={roomSystemIcon} data-testid="roomSystemIcon" />
              </div>
              <h3>
                <FormattedMessage id="components.roomSystems.header" defaultMessage="Room Systems" />
              </h3>
            </header>
          </div>
          <div className={classes.contactsListContainer} ref="container">
            {this.props.loading && <div>
              <Spinner
                config={{
                  lines: 13,
                  length: 17,
                  width: 8,
                  radius: 20,
                  color: '#413675',
                  speed: 0.7
                }}
              />
            </div>}
            {contactsCount ? (
              <ReactInfinite
                id="contactsListContainer"
                className={classes.contactsList}
                onInfiniteLoad={this.props.fetchNext}
                infiniteLoadBeginEdgeOffset={REACT_INFINITE_LOAD_BEGIN_OFFSET}
                elementHeight={CONTACT_HEIGHT}
                containerHeight={this.state.height}
                useWindowAsScrollContainer={!this.state.height}
              >
                {this.props.contacts.map((contact, index) =>
                  <ContactListItem
                    key={contact.id}
                    contact={contact}
                    className={classes.contactListItem}
                    handleSelectContact={this.handleSelectContact}
                    handleVideoCall={this.props.handleVideoCall}
                    handleAudioCall={this.props.handleAudioCall}
                    handleSchedule={this.props.handleSchedule}
                    isFirstContact={index === 0}
                    isLastContact={contactsCount === (index + 1)}
                    selectedContact={selectedContact}
                    dataTestTag="rooms-contact"
                  />
                )}
              </ReactInfinite>
            ) :
              (<div className={classes.noContacts}>
                <FormattedMessage
                  id="components.roomSystems.noContacts"
                  defaultMessage="There are no matches for your selection."
                />
              </div>)}
          </div>
        </div>
        {this.renderRightPanel()}
      </div>
    );
  }
}

RoomSystems.propTypes = {
  contacts: PT.array.isRequired,
  currentUserId: PT.string,
  endRouteLoading: PT.func,
  fetchNext: PT.func,
  getSelectedContact: PT.func,
  handleAudioCall: PT.func.isRequired,
  handleRemoteModerate: PT.func,
  handleSchedule: PT.func.isRequired,
  handleVideoCall: PT.func.isRequired,
  loadInitialRooms: PT.func,
  loading: PT.bool,
  remoteModerationIsActive: PT.bool,
  remoteModerationIsActiveForThisMeeting: PT.func,
  scrollToSelected: PT.bool,
  userCanRemoteModerate: PT.func
};

export default RoomSystems;
